import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    if (!isHome) {
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: '事業紹介', id: 'services' },
    { label: '会社概要', id: 'about' },
    { label: 'お問い合わせ', id: 'contact' },
    { label: 'プライバシーポリシー', href: '/privacy-policy' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            onClick={() => {
              setMobileMenuOpen(false);
              if (isHome) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <span
              className={`font-serif text-lg md:text-xl font-bold tracking-wide transition-colors duration-300 ${
                scrolled ? 'text-axelia-text' : 'text-axelia-text'
              }`}
            >
              AXELIA
            </span>
            <span
              className={`text-[10px] md:text-xs font-sans transition-colors duration-300 ${
                scrolled ? 'text-axelia-text-muted' : 'text-axelia-text-secondary'
              }`}
            >
              株式会社
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {navLinks.map((link) =>
              link.href ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-axelia-accent ${
                    scrolled ? 'text-axelia-text-secondary' : 'text-axelia-text-secondary'
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                isHome ? (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id!)}
                    className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-axelia-accent cursor-pointer ${
                      scrolled ? 'text-axelia-text-secondary' : 'text-axelia-text-secondary'
                    }`}
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.id}
                    to={`/#${link.id}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-axelia-accent ${
                      scrolled ? 'text-axelia-text-secondary' : 'text-axelia-text-secondary'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )
            )}
          </div>

          {/* CTA Button Desktop */}
          <div className="hidden md:block">
            {isHome ? (
              <button
                onClick={() => scrollToSection('contact')}
                className="px-5 py-2.5 text-sm font-medium bg-axelia-text text-white rounded-full hover:bg-axelia-accent transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </button>
            ) : (
              <Link
                to="/#contact"
                className="px-5 py-2.5 text-sm font-medium bg-axelia-text text-white rounded-full hover:bg-axelia-accent transition-colors duration-300 whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="メニュー"
          >
            <i
              className={`ri-menu-4-line text-xl transition-colors ${
                scrolled ? 'text-axelia-text' : 'text-axelia-text'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ${
          mobileMenuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white/98 backdrop-blur-md border-t border-axelia-bg-warm px-6 py-5 flex flex-col gap-4">
          {navLinks.map((link) =>
            link.href ? (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-axelia-text-secondary py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ) : (
              isHome ? (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id!)}
                  className="text-left text-sm font-medium text-axelia-text-secondary py-2 cursor-pointer"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.id}
                  to={`/#${link.id}`}
                  className="text-left text-sm font-medium text-axelia-text-secondary py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )
          )}
          {isHome ? (
            <button
              onClick={() => scrollToSection('contact')}
              className="mt-2 px-5 py-3 text-sm font-medium bg-axelia-text text-white rounded-full text-center cursor-pointer"
            >
              お問い合わせする
            </button>
          ) : (
            <Link
              to="/#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-2 px-5 py-3 text-sm font-medium bg-axelia-text text-white rounded-full text-center"
            >
              お問い合わせする
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
